import type { QueryClient } from '@tanstack/vue-query';
import type { Components } from '@/api/generated/api';
import { EntryAttachmentType, type IEntryAttachmentTarget } from 'common/enum/entry.attachment.enum';
import { invalidateEntryAttachmentQueries } from '../attachments/entryAttachmentQueryInvalidation';
import { supportMeasureEntryKeys } from './supportMeasureEntry.keys';

export const invalidateSupportMeasureEntryQueries = async (
  queryClient: QueryClient,
  params: { entryId: number },
): Promise<void> => {
  await queryClient.invalidateQueries({
    queryKey: supportMeasureEntryKeys.filterStudents(params.entryId),
    exact: false,
  });
};

/** The measure detail includes its own attachment count and the counts of its protocol entries. */
export const invalidateSupportMeasureAttachmentDetails = async (
  queryClient: QueryClient,
  target: IEntryAttachmentTarget,
): Promise<void> => {
  if (target.entryType === EntryAttachmentType.SupportMeasure) {
    await queryClient.invalidateQueries({ queryKey: supportMeasureEntryKeys.detail(target.entryId), exact: true });
  } else if (target.entryType === EntryAttachmentType.SupportMeasureProtocol) {
    const details = queryClient.getQueriesData<Components.Schemas.SupportMeasureEntryDto>({
      queryKey: supportMeasureEntryKeys.details(),
    });
    await Promise.all(
      details
        .filter(([, entry]) => entry?.protocol.some(({ id }) => id === target.entryId))
        .map(([queryKey]) => queryClient.invalidateQueries({ queryKey, exact: true })),
    );
  }
};

/** Completing or reopening a measure changes canEdit for its own files and all of its protocol entries. */
export const invalidateSupportMeasureAttachmentPermissions = async (
  queryClient: QueryClient,
  entry: Pick<Components.Schemas.SupportMeasureEntryDto, 'id' | 'protocol'>,
): Promise<void> => {
  const targets = [
    { entryType: EntryAttachmentType.SupportMeasure, entryId: entry.id },
    ...entry.protocol.map(({ id }) => ({ entryType: EntryAttachmentType.SupportMeasureProtocol, entryId: id })),
  ];
  await Promise.all(targets.map((target) => invalidateEntryAttachmentQueries(queryClient, target)));
};
